import { useSocialMediaInfo } from "@/api/information";
import { useGetLocaleCurrency } from "@/api/live-conversion";
import { useGetPrograms } from "@/api/program";
import { GTMPageTracker } from "@/components/GTMPageTracket";
import { MountWhenVisible } from "@/components/MountWhenVisible";
import TourManager from "@/components/Tour/TourManager";
import { SUPPORTED_LANGUAGES } from "@/config/config";
import { TourProvider } from "@/contexts/TourContext";
import { NotFound } from "@/pages/errorPages/notFound";
import { isRTLLanguage } from "@/utils/helpers";
import { detectLanguage } from "@/utils/language-detection";
import { Outlet, ScrollRestoration, createRootRoute, redirect } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
const Footer = lazy(() => import("@/components/Footer").then((mod) => ({ default: mod.Footer })));

export const Route = createRootRoute({
  component: RootComponent,
  beforeLoad: async ({ location }) => {
    const pathname = location.pathname;
    // Check if path already starts with a known locale
    const pathSegments = pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];
    const hasLocalePrefix = SUPPORTED_LANGUAGES.includes(firstSegment as any);

    // If no locale prefix, redirect to locale-prefixed version
    if (!hasLocalePrefix) {
      const detectedLocale = await detectLanguage(location);

      // Preserve trailing slash behavior
      const trailingSlash = pathname.endsWith("/") && pathname.length > 1 ? "/" : "";
      const targetPath = `/${detectedLocale}${pathname === "/" ? "" : pathname}${trailingSlash}`;

      // Build search string from object if it has properties
      const searchParams = new URLSearchParams(location.search).toString();
      const search = searchParams ? `?${searchParams}` : "";

      // Preserve hash if present
      const hash = location.hash ? `#${location.hash}` : "";

      throw redirect({
        to: targetPath + search + hash,
        replace: true, // Don't clutter browser history
      });
    }
  },
  notFoundComponent: NotFound,
});

function RootComponent() {
  const { i18n } = useTranslation();
  const { data } = useGetLocaleCurrency();
  useSocialMediaInfo()
  useGetPrograms({
    ordering: "rank",
    page_size: "3",
    lang: i18n.language,
  });
  return (
    <TourProvider>
      <div dir={isRTLLanguage(i18n.language) ? "rtl" : "ltr"} data-currency={data?.localeCurrency}>
        <ScrollRestoration />
        {/* Sentinel at top intersects on first paint so Joyride chunk loads without waiting for scroll */}
        <MountWhenVisible rootMargin="0px">
          <TourManager />
        </MountWhenVisible>
        <Outlet />
        <MountWhenVisible className="w-full">
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
        </MountWhenVisible>
        <GTMPageTracker />
      </div>
    </TourProvider>
  );
}
